import {useContext, useEffect, useMemo, useRef} from 'react'
import {graphql, useFragment} from 'react-relay'

import {useAppDispatch, useAppSelector} from '../../../../../hooks/react-redux'
import {useAgentPoolIdParam} from '../../../../../hooks/routes'
import type {AgentPoolId} from '../../../../../types'
import {objectKeys} from '../../../../../utils/object'
import {subscribeOnOverallEvents} from '../../../../../utils/subscriber'
import {getQueueAllPoolsHref} from '../../../../pages/QueuePage/QueuePage.routes'

import type {QueueSidebarPoolIdsFragment$key} from './__generated__/QueueSidebarPoolIdsFragment.graphql'
import type {QueueSidebarPoolsFragment$key} from './__generated__/QueueSidebarPoolsFragment.graphql'
import {QueueSidebarSearchContext} from './QueueSidebar.contexts'
import {getPoolCounters} from './QueueSidebar.selectors'
import {queueSidebar} from './QueueSidebar.slices'
import type {
  QueueCountersByPool,
  QueueSidebarItemType,
  QueueSidebarLink,
  QueueSidebarPool,
} from './QueueSidebar.types'

const allPoolsLink: QueueSidebarLink = {
  type: 'queue-sidebar-link',
  id: 'all-agent-pools-link',
  title: 'All pools',
  level: 0,
  to: getQueueAllPoolsHref(),
  active: false,
}
const activeAllPoolsLink: QueueSidebarLink = {...allPoolsLink, active: true}

const poolsFragment = graphql`
  fragment QueueSidebarPoolsFragment on AgentPools {
    agentPool {
      rawId
      name
    }
  }
`

export function useQueueSidebarItems(
  poolsKey: QueueSidebarPoolsFragment$key | null,
): [QueueSidebarItemType[], number] {
  const activeAgentPoolId = useAgentPoolIdParam()
  const searchQuery = useContext(QueueSidebarSearchContext).searchQuery

  const poolCounters = useAppSelector(getPoolCounters)
  const {agentPool} = useFragment(poolsFragment, poolsKey) ?? {}
  return useMemo(() => {
    let activeItemIndex = -1
    const items: QueueSidebarItemType[] = []

    if (activeAgentPoolId == null) {
      activeItemIndex = items.push(activeAllPoolsLink) - 1
    } else {
      items.push(allPoolsLink)
    }

    agentPool?.forEach((pool, i) => {
      const {rawId, name = ''} = pool
      if (
        name == null ||
        (searchQuery != null && !name.toLowerCase().includes(searchQuery.toLowerCase()))
      ) {
        return
      }

      const active = activeAgentPoolId === pool.rawId
      items.push({
        type: 'queue-sidebar-pool',
        id: rawId,
        title: name,
        level: 0,
        active,
        counters: poolCounters[rawId!],
      } as QueueSidebarPool)

      if (active) {
        activeItemIndex = i
      }
    })

    return [items, activeItemIndex]
  }, [activeAgentPoolId, agentPool, poolCounters, searchQuery])
}

const poolIdsFragment = graphql`
  fragment QueueSidebarPoolIdsFragment on Query {
    agentPoolsRest {
      agentPool {
        rawId
      }
    }
  }
`

export function useSubscribeOnPoolCounters(
  key: QueueSidebarPoolIdsFragment$key | null,
  refetch: () => void,
) {
  const {agentPoolsRest} = useFragment(poolIdsFragment, key) ?? {}
  const prevPoolIds = useRef<AgentPoolId[]>([])
  const dispatch = useAppDispatch()
  useEffect(
    () =>
      subscribeOnOverallEvents(
        ['buildQueue/countersByPool'],
        (data: QueueCountersByPool | null | undefined) => {
          if (data == null) {
            return
          }

          dispatch(queueSidebar.actions.receiveCounters(data))
          const poolIds = objectKeys(data)

          if (
            poolIds.some(
              id =>
                !prevPoolIds.current.includes(id) &&
                !agentPoolsRest?.agentPool?.find(pool => pool.rawId === id),
            )
          ) {
            refetch()
          }

          prevPoolIds.current = poolIds
        },
      ),
    [agentPoolsRest?.agentPool, dispatch, refetch],
  )
}
